import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { navigate, useNavigationPrompt, useQueryParams } from "raviger";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";

import RadioInput from "@/components/ui/RadioInput";
import { Button } from "@/components/ui/button";
import DateField from "@/components/ui/date-field";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PhoneInput } from "@/components/ui/phone-input";
import { Textarea } from "@/components/ui/textarea";

import { usePatientContext } from "@/hooks/usePatientUser";

import { validateName } from "@/common/validation";

import { usePubSub } from "@/Utils/pubsubContext";
import mutate from "@/Utils/request/mutate";
import query from "@/Utils/request/query";
import { dateQueryString } from "@/Utils/utils";
import validators from "@/Utils/validators";
import GovtOrganizationSelector from "@/pages/Organization/components/GovtOrganizationSelector";
import { PublicPatientRead } from "@/types/emr/patient/patient";
import publicPatientApi from "@/types/emr/patient/publicPatientApi";
import PublicAppointmentApi from "@/types/scheduling/PublicAppointmentApi";

import {
  BLOOD_GROUP_CHOICES,
  PATIENT_GENDER_TYPES,
  PATIENT_GENDERS,
} from "@/common/constants";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import { getCurrencySymbol } from "@/components/ui/monetary-display";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  BloodGroupChoices,
  PatientMaritalStatus,
  PatientRelationshipType,
} from "@/types/emr/patient/patient";

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  clearPublicPendingBooking,
  getPublicPendingBookingStorageKey,
  loadPublicPendingBooking,
} from "@/pages/PublicAppointments/pendingBookingStorage";
import { getPriceBreakdown } from "@/types/base/monetaryComponent/monetaryComponent";
import publicFacilityApi from "@/types/facility/publicFacilityApi";
import { Loader2, Timer } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

type PatientRegistrationProps = {
  facilityId: string;
  staffId: string;
};

type ChargeItem = {
  title: string;
  amount: string;
};

type ChargeDialogData = {
  registrationCharge?: ChargeItem;
};

const getAmount = (value: string | undefined) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

const normalizePhoneNumber = (value?: string) =>
  value?.replace(/\D/g, "") ?? "";

const getComparablePhoneNumber = (value?: string) =>
  normalizePhoneNumber(value).replace(/^91(?=\d{10}$)/, "");

const getSecondsLeft = (expiry: string | null) => {
  if (!expiry) return null;

  return Math.max(
    0,
    Math.floor((new Date(expiry).getTime() - Date.now()) / 1000),
  );
};

const formatTimer = (secondsLeft: number | null) => {
  if (secondsLeft === null) return null;

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

export function PatientRegistration(props: PatientRegistrationProps) {
  const [isChargeDialogOpen, setIsChargeDialogOpen] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<any>(null);
  const [chargeDialogData, setChargeDialogData] =
    useState<ChargeDialogData | null>(null);
  const [reservationExpired, setReservationExpired] = useState(false);

  const RELATIONSHIP_OPTIONS: {
    labelKey: string;
    value: PatientRelationshipType;
  }[] = [
    { value: "father", labelKey: "father" },
    { value: "mother", labelKey: "mother" },
    { value: "spouse", labelKey: "spouse" },
  ];

  const MARITAL_STATUS_OPTIONS: {
    labelKey: string;
    value: PatientMaritalStatus;
  }[] = [
    { value: "single", labelKey: "single" },
    { value: "married", labelKey: "married" },
    { value: "divorced", labelKey: "divorced" },
    { value: "widowed", labelKey: "widowed" },
    { value: "separated", labelKey: "separated" },
  ];

  const ID_TYPE_OPTIONS = [
    { value: "aadhaar", label: "Aadhaar ID" },
    { value: "pan", label: "PAN Card" },
    { value: "passport", label: "Passport" },
    { value: "voter", label: "Voter ID" },
    { value: "driving_license", label: "Driving License" },
  ];

  const { staffId } = props;
  const { t } = useTranslation();
  const [{ slotId, date, reason, consultationTitle, consultationAmount }] =
    useQueryParams();
  const hasConsultationCharge =
    typeof consultationAmount === "string" &&
    consultationAmount.trim() !== "" &&
    consultationAmount.toLowerCase() !== "undefined" &&
    consultationAmount.toLowerCase() !== "null" &&
    Number.isFinite(Number(consultationAmount));
  const patientSelectQuery = {
    slotId,
    date,
    reason,
    ...(typeof consultationTitle === "string" ? { consultationTitle } : {}),
    ...(typeof consultationAmount === "string" ? { consultationAmount } : {}),
  };
  const schedulePath = `/facility/${props.facilityId}/appointments/${staffId}/book-appointment`;

  const queryClient = useQueryClient();

  const { publish } = usePubSub();

  const patientUserContext = usePatientContext();
  const tokenData = patientUserContext?.tokenData;
  const storageKey = useMemo(
    () =>
      getPublicPendingBookingStorageKey(
        props.facilityId,
        staffId,
        slotId ?? "",
        tokenData?.phoneNumber ?? "",
      ),
    [props.facilityId, staffId, slotId, tokenData?.phoneNumber],
  );
  const persistedPendingBooking = useMemo(
    () => loadPublicPendingBooking(storageKey),
    [storageKey],
  );
  const [pendingAppointmentId, setPendingAppointmentId] = useState<
    string | null
  >(persistedPendingBooking?.appointmentId ?? null);
  const [pendingExpiry, setPendingExpiry] = useState<string | null>(
    persistedPendingBooking?.pendingExpiry ?? null,
  );
  const [secondsLeft, setSecondsLeft] = useState<number | null>(() =>
    getSecondsLeft(persistedPendingBooking?.pendingExpiry ?? null),
  );
  const expiryHandledRef = useRef(false);
  const { data: facilityData, isLoading: isFacilityLoading } = useQuery({
    queryKey: ["facility", props.facilityId],
    queryFn: query(publicFacilityApi.getAny, {
      pathParams: { id: props.facilityId },
      silent: true,
    }),
    enabled: !!props.facilityId,
  });
  const registrationChargeDefinition =
    facilityData?.registration_charge_item_definition ?? null;
  const registrationChargeAmount = registrationChargeDefinition
    ? getPriceBreakdown(registrationChargeDefinition.price_components).total
    : undefined;
  const currencySymbol = getCurrencySymbol();
  const timerLabel = formatTimer(secondsLeft);

  const patientSchema = z
    .object({
      name: z
        .string({ required_error: t("field_required") })
        .trim()
        .min(1, t("field_required"))
        .refine(validateName, t("min_char_length_error", { min_length: 3 })),

      gender: z.enum(PATIENT_GENDERS, { required_error: t("field_required") }),

      relationship_type: z.enum(["father", "mother", "spouse"], {
        required_error: t("field_required"),
      }),

      relationship_name: z
        .string({ required_error: t("field_required") })
        .trim()
        .min(1, t("field_required")),

      marital_status: z
        .enum(["single", "married", "divorced", "widowed", "separated"])
        .optional(),
      blood_group: z.nativeEnum(BloodGroupChoices).optional(),
      address: z.string().optional(),
      permanent_address_same_as_address: z.boolean(),
      permanent_address: z.string().optional(),

      emergency_phone_number_same_as_phone_number: z.boolean(),
      emergency_phone_number: validators().phoneNumber.optional,

      age: z.string().optional(),
      date_of_birth: z.date().or(z.string()).optional(),
      pincode: validators().pincode.optional(),
      geo_organization: z.string().min(1, t("organization_required")),

      ageInputType: z.enum(["age", "date_of_birth"]),
      id_type: z.string().optional(),
      id_value: z.string().optional(),
      abha_id: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      const field = data.ageInputType === "age" ? "age" : "date_of_birth";
      if (!data[field]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("field_required"),
          path: [field],
        });
        return;
      }
      if (
        field === "age" &&
        data.age &&
        !isNaN(Number(data.age)) &&
        Number(data.age) < 0
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("age_less_than_0"),
          path: ["age"],
        });
      }

      if (
        !data.permanent_address_same_as_address &&
        !data.permanent_address?.trim()
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("field_required"),
          path: ["permanent_address"],
        });
      }

      if (
        !data.emergency_phone_number_same_as_phone_number &&
        !data.emergency_phone_number
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("field_required"),
          path: ["emergency_phone_number"],
        });
      }

      if (
        !data.emergency_phone_number_same_as_phone_number &&
        data.emergency_phone_number &&
        getComparablePhoneNumber(data.emergency_phone_number) ===
          getComparablePhoneNumber(tokenData.phoneNumber)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("emergency_phone_number_must_differ", {
            defaultValue:
              "Emergency phone number cannot be same as mobile number",
          }),
          path: ["emergency_phone_number"],
        });
      }
    });

  const formResolver = zodResolver(patientSchema);

  const form = useForm({
    resolver: formResolver,
    defaultValues: {
      name: "",
      ageInputType: "date_of_birth",
      address: "",
      permanent_address: "",
      permanent_address_same_as_address: true,
      emergency_phone_number: tokenData.phoneNumber,
      emergency_phone_number_same_as_phone_number: true,
      relationship_type: "father",
      relationship_name: "",
      marital_status: undefined,
      blood_group: undefined,
      id_type: "aadhaar",
      id_value: "",
      abha_id: "",
    },
  });

  const selectedIdType = form.watch("id_type");
  const selectedIdTypeLabel =
    ID_TYPE_OPTIONS.find((option) => option.value === selectedIdType)?.label ??
    "Government ID";
  useEffect(() => {
    setPendingAppointmentId(persistedPendingBooking?.appointmentId ?? null);
    setPendingExpiry(persistedPendingBooking?.pendingExpiry ?? null);
    setReservationExpired(false);
    expiryHandledRef.current = false;
  }, [persistedPendingBooking]);

  useEffect(() => {
    setSecondsLeft(getSecondsLeft(pendingExpiry));

    if (!pendingExpiry) return;

    const intervalId = window.setInterval(() => {
      setSecondsLeft(getSecondsLeft(pendingExpiry));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [pendingExpiry]);

  const { mutateAsync: cancelAppointment, isPending: isCancellingAppointment } =
    useMutation({
      mutationFn: mutate(PublicAppointmentApi.cancelAppointment, {
        headers: {
          Authorization: `Bearer ${tokenData.token}`,
        },
      }),
    });

  const { mutateAsync: createPatient, isPending: isCreatingPatient } =
    useMutation({
      mutationFn: mutate(publicPatientApi.create, {
        headers: {
          Authorization: `Bearer ${tokenData.token}`,
        },
      }),
    });

  const clearPendingReservation = () => {
    clearPublicPendingBooking(storageKey);
    setPendingAppointmentId(null);
    setPendingExpiry(null);
    expiryHandledRef.current = false;
  };

  const refreshPublicBookingState = async () => {
    await queryClient.invalidateQueries({
      queryKey: ["otp-patient"],
    });
    await queryClient.invalidateQueries({
      queryKey: ["appointment", tokenData.phoneNumber],
    });
    await queryClient.invalidateQueries({
      queryKey: ["public-slots", props.facilityId, staffId],
    });
    await queryClient.invalidateQueries({
      queryKey: ["public-availability-heatmap", props.facilityId, staffId],
    });
  };

  const navigateBackToSchedule = () => {
    navigate(schedulePath, {
      query: {
        slotId,
        date,
        reason,
      },
      replace: true,
    });
  };

  const navigateToPatientSelect = (
    selectedPatientId?: string,
    replace = false,
  ) => {
    navigate(
      `/facility/${props.facilityId}/appointments/${staffId}/patient-select`,
      {
        query: {
          ...patientSelectQuery,
          ...(selectedPatientId ? { selectedPatient: selectedPatientId } : {}),
        },
        replace,
      },
    );
  };

  const submitPatientRegistration = async (data: any) => {
    const formattedData = {
      name: data.name,
      gender: PATIENT_GENDERS.includes(
        data.gender as (typeof PATIENT_GENDERS)[number],
      )
        ? (data.gender as (typeof PATIENT_GENDERS)[number])
        : undefined,

      relationship_type: data.relationship_type,
      relationship_name: data.relationship_name.trim(),
      marital_status: data.marital_status || undefined,

      blood_group: data.blood_group,

      address: data.address || "",
      permanent_address: data.permanent_address_same_as_address
        ? data.address
        : data.permanent_address,

      emergency_phone_number: data.emergency_phone_number_same_as_phone_number
        ? undefined
        : data.emergency_phone_number,

      date_of_birth:
        data.ageInputType === "date_of_birth"
          ? dateQueryString(data.date_of_birth)
          : undefined,

      age: data.ageInputType === "age" ? Number(data.age) : undefined,

      ...(data.pincode ? { pincode: data.pincode } : {}),
      geo_organization: data.geo_organization,
    };

    const createdPatient = (await createPatient({
      ...formattedData,
      facility: props.facilityId,
    } as any)) as PublicPatientRead;

    publish("patient:upsert", createdPatient);

    await queryClient.invalidateQueries({
      queryKey: ["otp-patient", tokenData.phoneNumber],
    });

    if (
      hasConsultationCharge &&
      (!pendingAppointmentId || secondsLeft === 0 || reservationExpired)
    ) {
      clearPendingReservation();
      await refreshPublicBookingState();
      toast.error(
        t("patient_registered_but_slot_reservation_expired", {
          defaultValue:
            "Patient registration was saved, but the slot reservation expired. Please choose the slot again.",
        }),
      );
      navigateBackToSchedule();
      return;
    }

    toast.success(
      t("patient_registration_success", {
        defaultValue: "Patient Registered Successfully",
      }),
    );
    navigateToPatientSelect(createdPatient.id, true);
  };

  const onSubmit = form.handleSubmit((data) => {
    const isNewPatientRegistration = true;

    const registrationApplicable =
      isNewPatientRegistration && !!registrationChargeDefinition;
    if (!registrationApplicable) {
      void submitPatientRegistration(data);
      return;
    }

    const nextDialogData: ChargeDialogData = {};

    if (registrationApplicable && registrationChargeDefinition) {
      nextDialogData.registrationCharge = {
        title: registrationChargeDefinition.title,
        amount: registrationChargeAmount ?? "0",
      };
    }

    setPendingFormData(data);
    setChargeDialogData(nextDialogData);
    setIsChargeDialogOpen(true);
  });

  const handleChargeDialogConfirm = () => {
    if (!pendingFormData) return;

    const formData = pendingFormData;

    setIsChargeDialogOpen(false);
    setPendingFormData(null);
    setChargeDialogData(null);
    void submitPatientRegistration(formData);
  };

  const handleChargeDialogCancel = () => {
    setIsChargeDialogOpen(false);
    setPendingFormData(null);
    setChargeDialogData(null);
  };

  // TODO: Use useBlocker hook after switching to tanstack router
  // https://tanstack.com/router/latest/docs/framework/react/guide/navigation-blocking#how-do-i-use-navigation-blocking
  useNavigationPrompt(
    form.formState.isDirty && !isCreatingPatient,
    t("unsaved_changes"),
  );

  useEffect(() => {
    if (
      !hasConsultationCharge ||
      !pendingAppointmentId ||
      secondsLeft !== 0 ||
      expiryHandledRef.current
    ) {
      return;
    }

    expiryHandledRef.current = true;
    void (async () => {
      try {
        await cancelAppointment({ appointment: pendingAppointmentId });
      } catch {
        // Ignore cleanup errors and still reset the local reservation state.
      }

      clearPendingReservation();
      setReservationExpired(true);
      await refreshPublicBookingState();
      toast.error(
        t("slot_reservation_expired", {
          defaultValue:
            "Slot reservation expired. Please choose the slot again.",
        }),
      );
    })();
  }, [
    cancelAppointment,
    hasConsultationCharge,
    pendingAppointmentId,
    refreshPublicBookingState,
    secondsLeft,
    t,
  ]);

  const renderReservationTimer = () => {
    if (!hasConsultationCharge) return null;

    const reservationStatus = reservationExpired
      ? t("slot_reservation_expired", {
          defaultValue:
            "Slot reservation expired. Please choose the slot again.",
        })
      : pendingAppointmentId
        ? t("slot_reserved_until_payment", {
            defaultValue: "This slot is reserved while you complete payment.",
          })
        : t("slot_reservation_missing", {
            defaultValue:
              "This reservation was not found. Please return and choose the slot again.",
          });

    return (
      <div className="mb-6 flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50/80 px-4 py-3 text-sm text-amber-900">
        <div className="font-medium">{reservationStatus}</div>
        {pendingAppointmentId && timerLabel && (
          <div className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-white px-3 py-1 font-medium text-amber-700">
            <Timer className="size-4" />
            {timerLabel}
          </div>
        )}
      </div>
    );
  };

  // const [showAutoFilledPincode, setShowAutoFilledPincode] = useState(false);

  return (
    <>
      <div className="container mx-auto p-4 max-w-4xl flex justify-start">
        <Button
          variant="outline"
          className="border border-gray-300"
          type="button"
          onClick={() =>
            navigate(
              `/facility/${props.facilityId}/appointments/${staffId}/patient-select`,
              {
                query: patientSelectQuery,
              },
            )
          }
        >
          <span className="text-sm underline">{t("back")}</span>
        </Button>
      </div>
      <Form {...form}>
        <form onSubmit={onSubmit} className="mx-auto space-y-6">
          <div className="container mx-auto p-4 max-w-3xl">
            {renderReservationTimer()}

            <h2 className="text-xl font-semibold">
              {t("patient_registration")}
            </h2>

            <Accordion
              type="multiple"
              className="mt-4 flex flex-col gap-6"
              defaultValue={["patient-basics", "additional-details"]}
            >
              <AccordionItem
                value="patient-basics"
                className="bg-white flex flex-col gap-4 p-6 shadow rounded-md"
              >
                <AccordionTrigger>
                  <h5 className="text-lg font-semibold">
                    1: {t("patient_basics")}
                  </h5>
                </AccordionTrigger>

                <AccordionContent className="px-1 flex flex-col gap-6">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel aria-required>{t("patient_name")}</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder={t("type_name")} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="relationship_type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel aria-required>
                          {t("father_mother_spouse")}
                        </FormLabel>
                        <FormControl>
                          <RadioInput
                            {...field}
                            onValueChange={field.onChange}
                            value={field.value ?? ""}
                            required={true}
                            options={RELATIONSHIP_OPTIONS.map((option) => ({
                              value: option.value,
                              label: t(option.labelKey),
                            }))}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="relationship_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel aria-required>
                          {t("father_mother_spouse_name")}
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder={t("enter_father_mother_spouse_name")}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="ageInputType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel aria-required>
                          {t("date_of_birth_or_age")}
                        </FormLabel>
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                          <Tabs
                            value={field.value}
                            onValueChange={field.onChange}
                            className="shrink-0"
                          >
                            <TabsList className="mt-1 w-fit gap-6 md:mt-px">
                              <TabsTrigger value="date_of_birth">
                                {t("date")}
                              </TabsTrigger>
                              <TabsTrigger value="age">{t("age")}</TabsTrigger>
                            </TabsList>
                          </Tabs>

                          {field.value === "date_of_birth" && (
                            <FormField
                              control={form.control}
                              name="date_of_birth"
                              render={({ field }) => (
                                <FormItem className="w-full">
                                  <FormControl>
                                    <DateField
                                      date={
                                        field.value
                                          ? new Date(field.value)
                                          : undefined
                                      }
                                      onChange={(date) =>
                                        field.onChange(dateQueryString(date))
                                      }
                                      hideLabels
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          )}

                          {field.value === "age" && (
                            <FormField
                              control={form.control}
                              name="age"
                              render={({ field }) => (
                                <FormItem className="relative w-full md:col-span-2">
                                  <div className="relative">
                                    <FormControl>
                                      <Input
                                        type="number"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        {...field}
                                        placeholder={t("type_patient_age")}
                                      />
                                    </FormControl>
                                    {field.value && (
                                      <span className="absolute right-9 top-3.25 text-xs text-gray-500 md:top-2.5">
                                        {`${t("year_of_birth")}: `}
                                        {new Date().getFullYear() -
                                          Number(field.value)}
                                      </span>
                                    )}
                                  </div>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          )}
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="gender"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel aria-required>{t("sex")}</FormLabel>
                        <FormControl>
                          <RadioInput
                            {...field}
                            onValueChange={field.onChange}
                            required={true}
                            options={PATIENT_GENDER_TYPES.map((g) => ({
                              value: g.id,
                              label: t(`GENDER__${g.id}`),
                            }))}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="marital_status"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("marital_status")}</FormLabel>
                          <Select
                            value={field.value ?? ""}
                            onValueChange={(value) =>
                              field.onChange(
                                value === "__unset__" ? undefined : value,
                              )
                            }
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue
                                  placeholder={t("select_marital_status")}
                                />
                              </SelectTrigger>
                            </FormControl>

                            <SelectContent>
                              <SelectItem value="__unset__">
                                {t("not_specified")}
                              </SelectItem>

                              {MARITAL_STATUS_OPTIONS.map((option) => (
                                <SelectItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {t(option.labelKey)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>

                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid gap-2">
                      <Label>
                        {t("mobile_number")}
                        <span className="text-red-500"> *</span>
                      </Label>
                      <PhoneInput
                        value={tokenData.phoneNumber}
                        onChange={() => undefined}
                        readOnly
                        disabled
                        className="opacity-100 [&_*]:opacity-100"
                      />
                    </div>
                  </div>

                  <FormField
                    control={form.control}
                    name="geo_organization"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <GovtOrganizationSelector
                            {...field}
                            requiredDepth={1}
                            authToken={tokenData.token}
                            className="grid grid-cols-1 md:grid-cols-2 gap-4"
                            levelClassName="mt-0"
                            onChange={(value) => field.onChange(value)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </AccordionContent>
              </AccordionItem>

              <AccordionItem
                value="additional-details"
                className="bg-white flex flex-col gap-4 p-6 shadow rounded-md"
              >
                <AccordionTrigger>
                  <h5 className="text-lg font-semibold">
                    2: {t("additional_details")}
                  </h5>
                </AccordionTrigger>

                <AccordionContent className="grid grid-cols-1 gap-4 px-1 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="address"
                    render={({ field }) => (
                      <FormItem className="flex flex-col md:col-span-2">
                        <FormLabel className="min-h-5 leading-5">
                          {t("address")}
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            placeholder={t("type_address")}
                            onChange={(e) => {
                              field.onChange(e);

                              if (
                                form.getValues(
                                  "permanent_address_same_as_address",
                                )
                              ) {
                                form.setValue(
                                  "permanent_address",
                                  e.target.value,
                                  { shouldDirty: true },
                                );
                              }
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="permanent_address_same_as_address"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-2 md:col-span-2">
                        <FormControl>
                          <Checkbox
                            checked={!field.value}
                            onCheckedChange={(value) => field.onChange(!value)}
                          />
                        </FormControl>
                        <FormLabel>
                          {t(
                            "permanent_address_is_different_from_current_address",
                          )}
                        </FormLabel>
                      </FormItem>
                    )}
                  />

                  {!form.watch("permanent_address_same_as_address") && (
                    <FormField
                      control={form.control}
                      name="permanent_address"
                      render={({ field }) => (
                        <FormItem className="md:col-span-2">
                          <FormLabel aria-required>
                            {t("permanent_address")}
                          </FormLabel>
                          <FormControl>
                            <Textarea
                              {...field}
                              placeholder={t("type_address")}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  <div className="col-span-full grid w-full grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="pincode"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel className="min-h-5 leading-5">
                            {t("pincode")}
                          </FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              className="w-full"
                              placeholder={t("enter_pincode")}
                              onChange={(e) => {
                                const value = e.target.value
                                  ? Number(e.target.value)
                                  : undefined;
                                field.onChange(value);
                              }}
                              inputMode="numeric"
                              pattern="[0-9]*"
                              type="number"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="blood_group"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="min-h-5 leading-5">
                            {t("blood_group")}
                          </FormLabel>
                          <Select
                            value={field.value ?? ""}
                            onValueChange={field.onChange}
                          >
                            <FormControl>
                              <SelectTrigger className="w-full">
                                <SelectValue
                                  placeholder={t("select_blood_group")}
                                />
                              </SelectTrigger>
                            </FormControl>

                            <SelectContent>
                              {BLOOD_GROUP_CHOICES.map((bg) => (
                                <SelectItem key={bg.id} value={bg.id}>
                                  {bg.text}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>

                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="emergency_phone_number_same_as_phone_number"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-2 md:col-span-2">
                        <FormControl>
                          <Checkbox
                            checked={!field.value}
                            onCheckedChange={(value) => {
                              const useDifferentEmergencyContact =
                                Boolean(value);
                              const nextSameAsPhone =
                                !useDifferentEmergencyContact;

                              field.onChange(nextSameAsPhone);

                              form.setValue(
                                "emergency_phone_number",
                                nextSameAsPhone ? tokenData.phoneNumber : "",
                                {
                                  shouldDirty: true,
                                },
                              );
                              form.clearErrors("emergency_phone_number");
                            }}
                          />
                        </FormControl>
                        <FormLabel>
                          {t("use_different_emergency_contact_number")}
                        </FormLabel>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {!form.watch(
                    "emergency_phone_number_same_as_phone_number",
                  ) && (
                    <div className="col-span-full w-full">
                      <FormField
                        control={form.control}
                        name="emergency_phone_number"
                        render={({ field }) => (
                          <FormItem className="w-full">
                            <FormLabel
                              aria-required
                              className="min-h-5 leading-5"
                            >
                              {t("emergency_phone_number")}
                            </FormLabel>
                            <FormControl>
                              <PhoneInput
                                {...field}
                                value={field.value ?? ""}
                                placeholder={t("enter_phone_number")}
                                className="w-full"
                                onChange={(value) => {
                                  const phoneValue = value ?? "";
                                  field.onChange(phoneValue);

                                  if (!phoneValue.trim()) {
                                    form.clearErrors("emergency_phone_number");
                                  }
                                }}
                                onBlur={() => {
                                  field.onBlur();

                                  const value = (
                                    form.getValues("emergency_phone_number") ??
                                    ""
                                  ).trim();

                                  if (!value) {
                                    form.clearErrors("emergency_phone_number");
                                    return;
                                  }

                                  void form.trigger("emergency_phone_number");
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}

                  <FormField
                    control={form.control}
                    name="id_type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="min-h-5 leading-5">
                          {t("id_type")}
                        </FormLabel>
                        <Select
                          value={field.value ?? "aadhaar"}
                          onValueChange={(value) => {
                            field.onChange(value);
                            form.setValue("id_value", "", {
                              shouldDirty: true,
                            });
                            form.clearErrors("id_value");
                          }}
                        >
                          <FormControl>
                            <SelectTrigger ref={field.ref} className="w-full">
                              <SelectValue placeholder="Select ID type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {ID_TYPE_OPTIONS.map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormDescription className="min-h-10">
                          {t("id_type_description")}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="id_value"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="min-h-5 leading-5">
                          {selectedIdTypeLabel}
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            value={field.value ?? ""}
                            className="h-9 w-full text-sm"
                            placeholder={t("enter_identifier_value")}
                            onChange={(e: any) => {
                              const v = e?.target ? e.target.value : e;
                              field.onChange(v);
                              if (!v?.toString().trim()) {
                                form.clearErrors("id_value");
                              }
                            }}
                            onBlur={() => {
                              field.onBlur();
                              const value = (form.getValues("id_value") ?? "")
                                .toString()
                                .trim();
                              if (!value) {
                                form.clearErrors("id_value");
                                return;
                              }
                              void form.trigger("id_value");
                            }}
                            onKeyDown={(e: any) => {
                              if (e.key === "Tab" || e.key === "Enter") {
                                field.onBlur();
                                const value = (form.getValues("id_value") ?? "")
                                  .toString()
                                  .trim();
                                if (!value) {
                                  form.clearErrors("id_value");
                                  return;
                                }
                                void form.trigger("id_value");
                              }
                            }}
                          />
                        </FormControl>
                        <FormDescription className="min-h-10">
                          {selectedIdTypeLabel === "Aadhaar ID"
                            ? "UIDAI Aadhaar Number"
                            : "\u00A0"}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="abha_id"
                    render={({ field }) => (
                      <FormItem className="col-span-full">
                        <FormLabel className="min-h-5 leading-5">
                          {t("abha_id")}
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            value={field.value ?? ""}
                            className="w-full"
                            placeholder={t("enter_identifier_value")}
                            onChange={(e: any) => {
                              const v = e?.target ? e.target.value : e;
                              field.onChange(v);
                              if (!v?.toString().trim()) {
                                form.clearErrors("abha_id");
                              }
                            }}
                            onBlur={() => {
                              field.onBlur();
                              const value = (form.getValues("abha_id") ?? "")
                                .toString()
                                .trim();
                              if (!value) {
                                form.clearErrors("abha_id");
                                return;
                              }
                              void form.trigger("abha_id");
                            }}
                            onKeyDown={(e: any) => {
                              if (e.key === "Tab" || e.key === "Enter") {
                                field.onBlur();
                                const value = (form.getValues("abha_id") ?? "")
                                  .toString()
                                  .trim();
                                if (!value) {
                                  form.clearErrors("abha_id");
                                  return;
                                }
                                void form.trigger("abha_id");
                              }
                            }}
                          />
                        </FormControl>
                        <FormDescription>
                          {t("abha_id_description")}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          <div className="bg-secondary-200 pt-3 pb-8">
            <div className="mx-auto mt-4 flex max-w-3xl flex-col gap-2 px-4 sm:flex-row sm:justify-center">
              <Button
                variant="white"
                className="w-full sm:w-60"
                type="button"
                onClick={() =>
                  navigate(
                    `/facility/${props.facilityId}/appointments/${staffId}/patient-select`,
                    {
                      query: patientSelectQuery,
                    },
                  )
                }
              >
                <span className="bg-linear-to-b from-white/15 to-transparent" />
                {t("cancel")}
              </Button>

              <Button
                variant="primary_gradient"
                className="w-full sm:w-60"
                type="submit"
                disabled={
                  isFacilityLoading ||
                  isCreatingPatient ||
                  isCancellingAppointment
                }
              >
                <span className="bg-linear-to-b from-white/15 to-transparent" />
                {(isCreatingPatient || isCancellingAppointment) && (
                  <Loader2 className="mr-2 size-4 animate-spin" />
                )}
                {t("register_patient")}
              </Button>
            </div>
          </div>
        </form>
      </Form>
      <Dialog open={isChargeDialogOpen} onOpenChange={setIsChargeDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Charges</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {chargeDialogData?.registrationCharge && (
              <div className="rounded-md border p-3">
                <div className="font-medium">
                  {chargeDialogData.registrationCharge.title}
                </div>
                <div className="text-sm text-gray-600">
                  {currencySymbol} {chargeDialogData.registrationCharge.amount}
                </div>
              </div>
            )}

            <div className="border-t pt-3 font-semibold">
              Total: {currencySymbol}{" "}
              {getAmount(chargeDialogData?.registrationCharge?.amount)}
            </div>

            <p className="text-sm text-gray-600">
              By continuing, you confirm the applicable charges and proceed with
              patient registration.
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              type="button"
              onClick={handleChargeDialogCancel}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleChargeDialogConfirm}>
              Confirm and Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
